import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dashboardService from '../../services/dashboardService';
import stageService from '../../services/stageService';
import { useProject } from '../../context/ProjectContext';
import { STAGE_WORKFLOW } from '../../utils/constants';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import './Dashboard.css';

function stageCodeToVisitsQueryStage(stageCode) {
  const code = Number(stageCode);
  if (Number.isNaN(code)) return 'schedule';
  if (code === STAGE_WORKFLOW.SCHEDULE) return 'schedule';
  if (code === STAGE_WORKFLOW.IN_PROGRESS) return 'onGoing';
  if (code === STAGE_WORKFLOW.COMPLETED) return 'closed';
  if (code > STAGE_WORKFLOW.IN_PROGRESS && code < STAGE_WORKFLOW.COMPLETED) return String(code);
  return 'schedule';
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentProject: contextProject } = useProject();
  const currentProject = contextProject && contextProject !== 'all' ? contextProject : null;

  const [stageCards, setStageCards] = useState([]);
  /** iStageCode values the current profile may open (ProfileActivityStageMapping + GET /api/stage). */
  const [allowedStageCodes, setAllowedStageCodes] = useState(() => new Set());
  const [pendingActionCount, setPendingActionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [currentProject]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      setAllowedStageCodes(new Set());

      const stagesPromise = stageService
        .getStages(currentProject)
        .then((stagesData) => {
          const rows = Array.isArray(stagesData) ? stagesData : [];
          const next = new Set(
            rows
              .map((s) => Number(s.stageCode ?? s.StageCode ?? s.id ?? s.Id))
              .filter((n) => !Number.isNaN(n))
          );
          setAllowedStageCodes(next);
        })
        .catch((e) => {
          console.error('Dashboard: profile stages for navigation', e);
          setAllowedStageCodes(new Set());
        });

      const [stageCardsData, kpisData] = await Promise.all([
        dashboardService.getStageCards(currentProject),
        dashboardService.getKPIs(currentProject),
      ]);
      await stagesPromise;

      setStageCards(Array.isArray(stageCardsData) ? stageCardsData : []);
      const kpis = kpisData || {};
      const count = kpis.totalPendingActionItems ?? kpis.TotalPendingActionItems;
      setPendingActionCount(typeof count === 'number' ? count : 0);
    } catch (err) {
      const errorMessage = err.response?.data?.error ||
                          err.message ||
                          'Failed to load dashboard data';
      setError(errorMessage);
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToVisitStage = useCallback(
    (stageQueryValue) => {
      const params = new URLSearchParams();
      params.set('stage', stageQueryValue);
      navigate(`/visits?${params.toString()}`);
    },
    [navigate]
  );

  const goToVisitIssues = useCallback(() => {
    navigate('/visits?tab=issues');
  }, [navigate]);

  if (loading) return <Spinner />;

  return (
    <div className="dashboard">
      {error && <Alert type="error" message={error} />}

      <div className="dashboard-cards">
            {stageCards.map((card, index) => {
              const code = Number(card.stageCode);
              const canClick = !Number.isNaN(code) && allowedStageCodes.has(code);
              const stageParam = stageCodeToVisitsQueryStage(card.stageCode);
              return (
                <DashboardCard
                  key={card.stageCode ?? card.orderNo}
                  icon={getIconForCard(card, index)}
                  title={card.displayName ?? ''}
                  value={typeof card.count === 'number' ? card.count : 0}
                  variant="default"
                  onClick={canClick ? () => goToVisitStage(stageParam) : undefined}
                  disabledReason={
                    !Number.isNaN(code) && !canClick
                      ? 'Your profile does not have access to this stage for the selected project.'
                      : undefined
                  }
                />
              );
            })}
            <DashboardCard
              icon="⚠️"
              title="Pending Issues"
              value={pendingActionCount}
              variant="error"
              onClick={goToVisitIssues}
            />
          </div>
      {!loading && stageCards.length === 0 && (
        <p className="dashboard-empty-message">
          {currentProject
            ? 'No stage data for this project. Ensure StageMst has stages for the selected study.'
            : 'Select a project (e.g. from Visit Scheduling or Sites) to see visit stage cards.'}
        </p>
      )}
    </div>
  );
};

// Different icon per stage (by stageCode or by display name); fallback by index
const STAGE_ICONS = {
  101: '📅',
  201: '🔄',
  301: '👁️',
  401: '🔍',
  9999: '✅',
};
const STAGE_ICONS_BY_NAME = {
  schedule: '📅',
  'in progress': '🔄',
  'first review': '👁️',
  'second review': '🔍',
  completed: '✅',
};
const FALLBACK_ICONS = ['📋', '📌', '📍', '📊', '📈', '🎯'];

const getIconForCard = (card, index) => {
  const code = card.stageCode;
  if (code != null && STAGE_ICONS[code]) return STAGE_ICONS[code];
  const name = (card.displayName || '').toLowerCase();
  if (STAGE_ICONS_BY_NAME[name]) return STAGE_ICONS_BY_NAME[name];
  return FALLBACK_ICONS[index % FALLBACK_ICONS.length];
};

const DashboardCard = ({
  icon,
  title,
  value,
  variant = 'default',
  onClick,
  disabledReason,
}) => {
  const locked = Boolean(disabledReason);
  const interactive = Boolean(onClick) && !locked;
  const classNames = ['dashboard-card', variant, interactive ? 'clickable' : '', locked ? 'disabled-card' : '']
    .filter(Boolean)
    .join(' ');

  const handleKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={classNames}
      onClick={interactive ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      title={disabledReason || (interactive ? `Open ${title} in Monitoring Visit & Report Management` : undefined)}
      aria-disabled={locked ? true : undefined}
    >
      <div className="card-icon">{icon}</div>
      <div className="card-title">{title}</div>
      <div className="card-value">{value}</div>
    </div>
  );
};

export default Dashboard;
